/**
 * Presentation helpers.
 *
 * Money is formatted from the minor-unit integer the API returns, never from a
 * float: the platform is careful to keep amounts exact all the way to the wire, and
 * the dashboard must not undo that in the last step.
 */

/** formatMoney renders minor units with a currency, e.g. "USD 1,234.56". */
export function formatMoney(minor: number, currency: string): string {
  const negative = minor < 0;
  const absolute = Math.abs(minor);
  const whole = Math.floor(absolute / 100);
  const fraction = absolute % 100;
  const grouped = whole.toLocaleString("en-ZW");
  return `${negative ? "−" : ""}${currency} ${grouped}.${String(fraction).padStart(2, "0")}`;
}

/** formatAmount renders minor units without the currency code, for table columns
 *  that already have a currency heading. */
export function formatAmount(minor: number): string {
  const negative = minor < 0;
  const absolute = Math.abs(minor);
  const whole = Math.floor(absolute / 100);
  const fraction = absolute % 100;
  return `${negative ? "−" : ""}${whole.toLocaleString("en-ZW")}.${String(fraction).padStart(2, "0")}`;
}

const HARARE = "Africa/Harare";

/** formatDateTime renders an instant in Zimbabwe local time, which is what an
 *  operator in Harare needs to reconcile against a bank statement. */
export function formatDateTime(iso?: string): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: HARARE,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

export function formatDate(iso?: string): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: HARARE,
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

/** formatRelative renders how long ago something happened, for activity feeds where
 *  "4 minutes ago" is more useful than a timestamp. */
export function formatRelative(iso?: string): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 45) return "just now";

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["second", 60],
    ["minute", 60],
    ["hour", 24],
    ["day", 7],
    ["week", 4.35],
    ["month", 12],
    ["year", Number.POSITIVE_INFINITY],
  ];

  let value = seconds;
  for (const [unit, size] of units) {
    if (Math.abs(value) < size) {
      return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(-Math.round(value), unit);
    }
    value /= size;
  }
  return formatDate(iso);
}

/** titleCase turns a snake_case enum into readable prose, e.g. "awaiting_otp" ->
 *  "Awaiting OTP". The acronym map exists because a naive title-case would render
 *  "Otp" and "Kyc", which look like mistakes. */
const ACRONYMS: Record<string, string> = {
  otp: "OTP",
  kyc: "KYC",
  api: "API",
  url: "URL",
  id: "ID",
  usd: "USD",
  zwg: "ZWG",
  pep: "PEP",
  cr14: "CR14",
  itf263: "ITF263",
};

export function titleCase(value?: string): string {
  if (!value) return "—";
  return value
    .split(/[_\s]+/)
    .map((word) => ACRONYMS[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** truncateId shortens an identifier for a dense table, keeping the prefix (which
 *  says what the object is) and the tail (which distinguishes it). */
export function truncateId(id: string, tail = 6): string {
  const separator = id.indexOf("_");
  if (separator === -1 || id.length <= separator + tail + 4) return id;
  return `${id.slice(0, separator + 1)}…${id.slice(-tail)}`;
}

/** formatBytes renders a file size for the KYC document list. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** methodLabel gives each payment rail its proper commercial name. */
export const METHOD_LABELS: Record<string, string> = {
  hosted: "Hosted checkout",
  ecocash: "EcoCash",
  innbucks: "InnBucks",
  smilecash: "SmileCash",
  omari: "O'mari",
  onemoney: "OneMoney",
  ewallet: "eWallet",
  card: "Visa / Mastercard",
};

export function methodLabel(method?: string): string {
  if (!method) return "—";
  return METHOD_LABELS[method] ?? titleCase(method);
}
